<script setup>
import { ref, computed, onMounted } from 'vue';
import { useFlowieStore } from '../../../stores/flowieStore.js';
import TeamBots from "./team-bots.vue";

const flowieStore = useFlowieStore();

const show = ref(false);
const loaded = ref(false);
const step = ref(0);
const saving = ref(false);

const businessName = ref('');
const businessDescription = ref('');
const targetAudience = ref('');
const primaryGoal = ref(null);
const hasWebsite = ref(false);
const websiteUrl = ref('');
const sellingTypes = ref([]);
const preferredTone = ref(null);
const contentLanguage = ref(null);
const customLanguage = ref('');

// Voice recording state
const isRecording = ref(false);
const isTranscribing = ref(false);
let mediaRecorder = null;
let audioChunks = [];

const totalSteps = 5;

const sellingOptions = [
    { value: 'services', labelKey: 'dashboard:businessContext.selling.services' },
    { value: 'consulting', labelKey: 'dashboard:businessContext.selling.consulting' },
    { value: 'coaching', labelKey: 'dashboard:businessContext.selling.coaching' },
    { value: 'courses', labelKey: 'dashboard:businessContext.selling.courses' },
    { value: 'digital_products', labelKey: 'dashboard:businessContext.selling.digitalProducts' },
    { value: 'physical_products', labelKey: 'dashboard:businessContext.selling.physicalProducts' },
    { value: 'retainers', labelKey: 'dashboard:businessContext.selling.retainers' },
    { value: 'subscriptions', labelKey: 'dashboard:businessContext.selling.subscriptions' },
    { value: 'events', labelKey: 'dashboard:businessContext.selling.events' },
    { value: 'saas', labelKey: 'dashboard:businessContext.selling.saas' },
];

const goalOptions = [
    { value: 'more_clients', labelKey: 'dashboard:businessContext.goal.moreClients' },
    { value: 'online_presence', labelKey: 'dashboard:businessContext.goal.onlinePresence' },
    { value: 'sell_products', labelKey: 'dashboard:businessContext.goal.sellProducts' },
    { value: 'grow_email_list', labelKey: 'dashboard:businessContext.goal.growEmailList' },
    { value: 'increase_revenue', labelKey: 'dashboard:businessContext.goal.increaseRevenue' },
    { value: 'build_authority', labelKey: 'dashboard:businessContext.goal.buildAuthority' },
    { value: 'automate_marketing', labelKey: 'dashboard:businessContext.goal.automateMarketing' },
    { value: 'launch_product', labelKey: 'dashboard:businessContext.goal.launchProduct' },
];

const toneOptions = [
    { value: 'friendly', labelKey: 'dashboard:businessContext.tone.friendly', descKey: 'dashboard:businessContext.tone.friendlyDesc' },
    { value: 'professional', labelKey: 'dashboard:businessContext.tone.professional', descKey: 'dashboard:businessContext.tone.professionalDesc' },
    { value: 'bold', labelKey: 'dashboard:businessContext.tone.bold', descKey: 'dashboard:businessContext.tone.boldDesc' },
];

const languageOptions = [
    { value: 'English', labelKey: 'dashboard:businessContext.language.english' },
    { value: 'Swedish', labelKey: 'dashboard:businessContext.language.swedish' },
    { value: 'Norwegian', labelKey: 'dashboard:businessContext.language.norwegian' },
    { value: 'other', labelKey: 'dashboard:businessContext.language.other' },
];

const canGoNext = computed(() => {
    if (step.value === 1) {
        return businessName.value.trim().length > 0
            && sellingTypes.value.length > 0
            && primaryGoal.value !== null;
    }
    if (step.value === 2) {
        return businessDescription.value.trim().length > 0
            && targetAudience.value.trim().length > 0;
    }
    if (step.value === 3) {
        return preferredTone.value !== null
            && contentLanguage.value !== null
            && (contentLanguage.value !== 'other' || customLanguage.value.trim().length > 0);
    }
    return true;
});

const resolvedLanguage = computed(() => {
    if (contentLanguage.value === 'other') return customLanguage.value.trim() || null;
    return contentLanguage.value;
});

const toggleSellingType = (value) => {
    const idx = sellingTypes.value.indexOf(value);
    if (idx >= 0) {
        sellingTypes.value.splice(idx, 1);
    } else {
        sellingTypes.value.push(value);
    }
};

// Voice recording
const startRecording = async () => {
    try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
        mediaRecorder = new MediaRecorder(stream, { mimeType });
        audioChunks = [];

        mediaRecorder.ondataavailable = (e) => {
            if (e.data.size > 0) audioChunks.push(e.data);
        };

        mediaRecorder.onstop = async () => {
            stream.getTracks().forEach(t => t.stop());
            isTranscribing.value = true;

            try {
                const blob = new Blob(audioChunks, { type: mediaRecorder.mimeType });
                const formData = new FormData();
                formData.append('audio', blob, 'recording.' + (mimeType === 'audio/webm' ? 'webm' : 'mp4'));

                const response = await fetch('/bo/voice-transcribe', {
                    method: 'POST',
                    headers: {
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
                    },
                    body: formData,
                });

                const data = await response.json();
                if (data.text) {
                    businessDescription.value = businessDescription.value
                        ? businessDescription.value + ' ' + data.text
                        : data.text;
                }
            } catch (err) {
                console.error('Voice transcription failed:', err);
            } finally {
                isTranscribing.value = false;
            }
        };

        mediaRecorder.start();
        isRecording.value = true;
    } catch (err) {
        console.error('Microphone access denied:', err);
    }
};

const stopRecording = () => {
    isRecording.value = false;
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop();
    }
};

const skip = () => {
    show.value = false;
};

const sayHelloToFlowie = (mode = 'chat') => {
    show.value = false;
    flowieStore.requestGreeting(mode);
};

const next = () => {
    if (step.value < totalSteps - 1) {
        step.value++;
    }
    if (step.value === totalSteps - 1) {
        submitData();
    }
};

const prev = () => {
    if (step.value > 0) {
        step.value--;
    }
};

const submitData = async () => {
    saving.value = true;
    try {
        if (hasWebsite.value && websiteUrl.value.trim().length > 0 && !/^https?:\/\//i.test(websiteUrl.value.trim())) {
            websiteUrl.value = 'https://' + websiteUrl.value.trim();
        }

        await axios.post('/bo/business-context', {
            business_name: businessName.value.trim() || null,
            business_description: businessDescription.value.trim() || null,
            target_audience: targetAudience.value.trim() || null,
            has_website: hasWebsite.value,
            website_url: hasWebsite.value ? (websiteUrl.value.trim() || null) : null,
            selling_types: sellingTypes.value.length > 0 ? sellingTypes.value : null,
            preferred_tone: preferredTone.value,
            content_language: resolvedLanguage.value,
            primary_goal: primaryGoal.value,
            brand_primary_color: null,
            preferred_design_style: null,
        });
    } catch (e) {
        // Context save failed, still show the done step
    } finally {
        saving.value = false;
    }
};

onMounted(async () => {
    try {
        const res = await axios.get('/bo/business-context/check');
        if (res.data.has_context) {
            show.value = false;
            loaded.value = true;
            return;
        }
        businessName.value = res.data.company_name || '';
        show.value = true;
        loaded.value = true;
    } catch (e) {
        loaded.value = true;
    }

    window.addEventListener('open-business-context-onboarding', () => {
        show.value = true;
        step.value = 0;
    });
});
</script>

<template>
    <Teleport to="body">
        <transition
            enter-active-class="transition-opacity duration-300"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition-opacity duration-200"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div v-if="show && loaded" class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                <div
                    class="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden flex flex-col transition-all duration-300"
                    style="max-height: 90vh;"
                >
                    <!-- Progress dots -->
                    <div v-if="step > 0 && step < totalSteps - 1" class="flex justify-center gap-1.5 pt-6 pb-2 px-6">
                        <div
                            v-for="i in totalSteps"
                            :key="i"
                            :class="[
                                'h-1.5 rounded-full transition-all duration-300',
                                i - 1 <= step ? 'bg-brandPrimary-500 w-6' : 'bg-gray-200 w-1.5'
                            ]"
                        ></div>
                    </div>

                    <!-- Content area -->
                    <div class="flex-1 overflow-y-auto px-8 py-6">

                        <!-- Step 0: Introduction with team bots -->
                        <div v-if="step === 0" class="text-center py-8">
                            <h2 class="text-2xl font-bold text-gray-900 mb-3">
                                <mf-translate domainkey="dashboard:businessContext.intro.title"></mf-translate>
                            </h2>
                            <p class="text-gray-500 max-w-sm mx-auto">
                                <mf-translate domainkey="dashboard:businessContext.intro.subtitle"></mf-translate>
                            </p>

                            <team-bots class="my-4"></team-bots>

                            <button-primary
                                @click="next"
                                size="xl"
                                class="w-full justify-center"
                            >
                                <mf-translate domainkey="dashboard:businessContext.intro.letsGo"></mf-translate>
                            </button-primary>
                            <button-secondary
                                @click="skip"
                                size="xl"
                                class="mt-3 w-full justify-center"
                            >
                                <mf-translate domainkey="dashboard:businessContext.intro.skip"></mf-translate>
                            </button-secondary>
                        </div>

                        <!-- Step 1: Business name + selling types + goal -->
                        <div v-if="step === 1">
                            <h3 class="text-xl font-semibold text-gray-900 mb-2">
                                <mf-translate domainkey="dashboard:businessContext.name.title"></mf-translate>
                            </h3>
                            <p class="text-sm text-gray-500 mb-5">
                                <mf-translate domainkey="dashboard:businessContext.name.subtitle"></mf-translate>
                            </p>
                            <input
                                v-model="businessName"
                                type="text"
                                :placeholder="_mft('dashboard:businessContext.name.placeholder')"
                                class="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:border-brandPrimary-500 focus:ring-1 focus:ring-brandPrimary-500"
                            />

                            <!-- Selling types -->
                            <div class="mt-6">
                                <label class="block text-sm font-medium text-gray-700 mb-2">
                                    <mf-translate domainkey="dashboard:businessContext.selling.title"></mf-translate>
                                </label>
                                <p class="text-xs text-gray-400 mb-3">
                                    <mf-translate domainkey="dashboard:businessContext.selling.subtitle"></mf-translate>
                                </p>
                                <div class="flex flex-wrap gap-2">
                                    <component
                                        v-for="option in sellingOptions"
                                        :key="option.value"
                                        :is="sellingTypes.includes(option.value) ? 'button-primary' : 'button-default'"
                                        @click="toggleSellingType(option.value)"
                                        type="button"
                                        size="md"
                                    >
                                        <mf-translate :domainkey="option.labelKey"></mf-translate>
                                    </component>
                                </div>
                            </div>

                            <!-- Primary goal -->
                            <div class="mt-6">
                                <label class="block text-sm font-medium text-gray-700 mb-2">
                                    <mf-translate domainkey="dashboard:businessContext.goal.title"></mf-translate>
                                </label>
                                <div class="grid grid-cols-2 gap-2">
                                    <component
                                        v-for="option in goalOptions"
                                        :key="option.value"
                                        :is="primaryGoal === option.value ? 'button-primary' : 'button-default'"
                                        @click="primaryGoal = option.value"
                                        type="button"
                                        size="md"
                                        class="justify-center"
                                    >
                                        <mf-translate :domainkey="option.labelKey"></mf-translate>
                                    </component>
                                </div>
                            </div>
                        </div>

                        <!-- Step 2: Business description + target audience -->
                        <div v-if="step === 2">
                            <h3 class="text-xl font-semibold text-gray-900 mb-2">
                                <mf-translate domainkey="dashboard:businessContext.description.title"></mf-translate>
                            </h3>
                            <p class="text-sm text-gray-500 mb-5">
                                <mf-translate domainkey="dashboard:businessContext.description.subtitle"></mf-translate>
                            </p>
                            <div class="relative">
                                <textarea
                                    v-model="businessDescription"
                                    data-description-textarea
                                    :placeholder="_mft('dashboard:businessContext.description.placeholder')"
                                    rows="4"
                                    class="w-full rounded-xl border border-gray-300 px-4 py-3 pr-12 text-sm focus:border-brandPrimary-500 focus:ring-1 focus:ring-brandPrimary-500 resize-none"
                                ></textarea>
                                <!-- Voice recording overlay -->
                                <div
                                    v-if="isRecording || isTranscribing"
                                    class="absolute inset-0 flex items-center justify-center bg-white/95 rounded-xl border border-red-200"
                                >
                                    <div v-if="isRecording" class="flex items-center gap-3">
                                        <div class="relative flex items-center justify-center w-8 h-8">
                                            <span class="absolute inline-flex w-full h-full rounded-full opacity-75 animate-ping bg-red-400/50"></span>
                                            <span class="relative flex items-center justify-center w-6 h-6 bg-red-500 rounded-full">
                                                <svg class="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                                                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                                                </svg>
                                            </span>
                                        </div>
                                        <div class="flex gap-[3px] items-end h-4">
                                            <span class="voice-bar w-[2px] rounded-full bg-red-400" style="animation-delay: 0ms"></span>
                                            <span class="voice-bar w-[2px] rounded-full bg-red-400" style="animation-delay: 120ms"></span>
                                            <span class="voice-bar w-[2px] rounded-full bg-red-400" style="animation-delay: 240ms"></span>
                                            <span class="voice-bar w-[2px] rounded-full bg-red-400" style="animation-delay: 360ms"></span>
                                        </div>
                                        <span class="text-xs font-medium text-red-500">{{ _mft('dashboard:flowie.recording') }}</span>
                                        <button-secondary
                                            @click="stopRecording"
                                            size="xs"
                                        >
                                            {{ _mft('dashboard:flowie.stopRecording') }}
                                        </button-secondary>
                                    </div>
                                    <div v-else class="flex items-center gap-2">
                                        <svg class="w-5 h-5 animate-spin text-brandPrimary-500" viewBox="0 0 24 24" fill="none">
                                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" />
                                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                        <span class="text-xs text-gray-500">{{ _mft('dashboard:flowie.transcribing') }}</span>
                                    </div>
                                </div>
                                <!-- Mic button -->
                                <span class="absolute bottom-3 right-3">
                                    <button-base
                                        v-if="!isRecording && !isTranscribing"
                                        @click="startRecording()"
                                        type="button"
                                        style-type="default"
                                        size="xs"
                                        class="!border-0 !bg-transparent !px-1.5 !py-1.5 !text-gray-400"
                                        :title="_mft('dashboard:businessContext.voice.record')"
                                    >
                                        <template #icon>
                                            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                                                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                                                <line x1="12" y1="19" x2="12" y2="23" />
                                                <line x1="8" y1="23" x2="16" y2="23" />
                                            </svg>
                                        </template>
                                    </button-base>
                                </span>
                            </div>

                            <!-- Target audience -->
                            <div class="mt-5">
                                <label class="block text-sm font-medium text-gray-700 mb-2">
                                    <mf-translate domainkey="dashboard:businessContext.audience.title"></mf-translate>
                                </label>
                                <input
                                    v-model="targetAudience"
                                    type="text"
                                    :placeholder="_mft('dashboard:businessContext.audience.placeholder')"
                                    class="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:border-brandPrimary-500 focus:ring-1 focus:ring-brandPrimary-500"
                                />
                            </div>
                        </div>

                        <!-- Step 3: Tone + Language + Website -->
                        <div v-if="step === 3">
                            <!-- Tone -->
                            <h3 class="text-xl font-semibold text-gray-900 mb-2">
                                <mf-translate domainkey="dashboard:businessContext.tone.title"></mf-translate>
                            </h3>
                            <p class="text-sm text-gray-500 mb-4">
                                <mf-translate domainkey="dashboard:businessContext.tone.subtitle"></mf-translate>
                            </p>
                            <div class="space-y-2 mb-6">
                                <component
                                    v-for="option in toneOptions"
                                    :key="option.value"
                                    :is="preferredTone === option.value ? 'button-primary' : 'button-default'"
                                    @click="preferredTone = option.value"
                                    type="button"
                                    size="xl"
                                    class="w-full !justify-start !text-left"
                                >
                                    <span class="block font-medium text-sm" :class="preferredTone === option.value ? 'text-wlPrimarContrast' : 'text-gray-900'">
                                        <mf-translate :domainkey="option.labelKey"></mf-translate>
                                    </span>
                                    <span class="block text-xs mt-0.5" :class="preferredTone === option.value ? 'text-wlPrimarContrast' : 'text-gray-400'">
                                        <mf-translate :domainkey="option.descKey"></mf-translate>
                                    </span>
                                </component>
                            </div>

                            <!-- Language -->
                            <h3 class="text-lg font-semibold text-gray-900 mb-2">
                                <mf-translate domainkey="dashboard:businessContext.language.title"></mf-translate>
                            </h3>
                            <div class="grid grid-cols-2 gap-3">
                                <component
                                    v-for="option in languageOptions"
                                    :key="option.value"
                                    :is="contentLanguage === option.value ? 'button-primary' : 'button-default'"
                                    @click="contentLanguage = option.value"
                                    type="button"
                                    size="lg"
                                    class="justify-center"
                                >
                                    <mf-translate :domainkey="option.labelKey"></mf-translate>
                                </component>
                            </div>
                            <transition
                                enter-active-class="transition-all duration-200"
                                enter-from-class="opacity-0 -translate-y-2"
                                enter-to-class="opacity-100 translate-y-0"
                                leave-active-class="transition-all duration-150"
                                leave-from-class="opacity-100"
                                leave-to-class="opacity-0"
                            >
                                <input
                                    v-if="contentLanguage === 'other'"
                                    v-model="customLanguage"
                                    type="text"
                                    :placeholder="_mft('dashboard:businessContext.language.otherPlaceholder')"
                                    class="mt-3 w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:border-brandPrimary-500 focus:ring-1 focus:ring-brandPrimary-500"
                                />
                            </transition>

                            <!-- Website inline -->
                            <div class="mt-6 pt-6 border-t border-gray-100">
                                <label class="block text-sm font-medium text-gray-700 mb-2">
                                    <mf-translate domainkey="dashboard:businessContext.website.title"></mf-translate>
                                </label>
                                <div class="flex gap-3 mb-3">
                                    <component
                                        @click="hasWebsite = true"
                                        :is="hasWebsite ? 'button-primary' : 'button-default'"
                                        type="button"
                                        size="md"
                                        class="flex-1 justify-center"
                                    >
                                        <mf-translate domainkey="dashboard:businessContext.website.yes"></mf-translate>
                                    </component>
                                    <component
                                        @click="hasWebsite = false; websiteUrl = ''"
                                        :is="!hasWebsite ? 'button-primary' : 'button-default'"
                                        type="button"
                                        size="md"
                                        class="flex-1 justify-center"
                                    >
                                        <mf-translate domainkey="dashboard:businessContext.website.no"></mf-translate>
                                    </component>
                                </div>
                                <transition
                                    enter-active-class="transition-all duration-200"
                                    enter-from-class="opacity-0 -translate-y-2"
                                    enter-to-class="opacity-100 translate-y-0"
                                    leave-active-class="transition-all duration-150"
                                    leave-from-class="opacity-100"
                                    leave-to-class="opacity-0"
                                >
                                    <input
                                        v-if="hasWebsite"
                                        v-model="websiteUrl"
                                        type="url"
                                        placeholder="https://www.example.com"
                                        class="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:border-brandPrimary-500 focus:ring-1 focus:ring-brandPrimary-500"
                                    />
                                </transition>
                            </div>
                        </div>

                        <!-- Step 4: Done! (was step 6) -->
                        <div v-if="step === 4" class="text-center py-8">
                            <div v-if="saving" class="flex items-center justify-center gap-2 mb-6">
                                <svg class="w-6 h-6 animate-spin text-brandPrimary-500" viewBox="0 0 24 24" fill="none">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" />
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                            </div>
                            <template v-else>
                                <div class="text-5xl mb-6">🎉</div>
                                <h2 class="text-2xl font-bold text-gray-900 mb-3">
                                    <mf-translate domainkey="dashboard:businessContext.done.title"></mf-translate>
                                </h2>
                                <p class="text-gray-500 max-w-sm mx-auto mb-6">
                                    <mf-translate domainkey="dashboard:businessContext.done.subtitle"></mf-translate>
                                </p>
                                <div class="flex flex-col items-center justify-center gap-3">
                                    <button-primary
                                        @click="sayHelloToFlowie('project')"
                                        size="xl"
                                    >
                                        <mf-translate domainkey="dashboard:flowie.newProject"></mf-translate>
                                    </button-primary>
                                </div>
                            </template>
                        </div>
                    </div>

                    <!-- Navigation buttons -->
                    <div v-if="step > 0 && step < totalSteps - 1" class="flex items-center justify-between px-8 py-5 border-t border-gray-100">
                        <button-default
                            @click="prev"
                            size="md"
                        >
                            <mf-translate domainkey="dashboard:businessContext.nav.back"></mf-translate>
                        </button-default>
                        <button-primary
                            @click="next"
                            :disabled="!canGoNext"
                            size="md"
                            class="justify-center"
                        >
                            <mf-translate :domainkey="step === totalSteps - 2 ? 'dashboard:businessContext.nav.finish' : 'dashboard:businessContext.nav.next'"></mf-translate>
                        </button-primary>
                    </div>
                </div>
            </div>
        </transition>
    </Teleport>
</template>

<style scoped>
.voice-bar {
    animation: voice-bar 0.8s ease-in-out infinite alternate;
}

@keyframes voice-bar {
    0% { height: 3px; }
    100% { height: 16px; }
}
</style>
